import 'react-toastify/dist/ReactToastify.css';
import React, { useState } from 'react';
import { HoverPopup } from './HoverPopup';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AiProvider } from './provider/AiProvider';
import { GetQuickshareParams, PROMPT_TYPE, PROMPT_USE_CASE } from './types';
import { Button } from 'react-covideo-common';
import { IconSparkleGradient } from './IconSparkeGradient';
import { IconSparkle } from './IconSparkle';
import { ModalGenerateText } from './modal/ModalGenerateText';
import { useAuth } from '../provider/auth';
import {
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from 'react-covideo-common/dist/components/Button/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // query options
      retry: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // mutation options
      retry: 0,
    },
  },
});

export interface MagicButtonProps {
  defaultPromptType: PROMPT_TYPE;
  defaultPromptUseCase?: PROMPT_USE_CASE;
  userInput?: string;
  defaultVin?: string;
  defaultRecipient?: string;
  margin?: string;
  videoId?: number | string;
  iconColor?: string | 'gradient';
  hoverPopup?: JSX.Element;
  hoverPosition?:
    | 'top'
    | 'bottom'
    | 'above'
    | 'bottom-left'
    | 'top-left'
    | 'right'
    | 'below';
  handleSubmitGeneratedContent?:
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  handleOnCopySuccess?:
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  disabled?: boolean;
  variant: BUTTON_VARIANT;
  text?: string;
  size?: BUTTON_SIZE;
}

export const MagicButton = ({
  handleSubmitGeneratedContent,
  handleOnCopySuccess,
  defaultPromptType,
  defaultPromptUseCase = PROMPT_USE_CASE.INITIAL_REACH_OUT,
  defaultRecipient = '',
  defaultVin = '',
  userInput = '',
  margin = '0',
  iconColor = 'white',
  videoId,
  hoverPopup,
  hoverPosition = 'above',
  disabled,
  variant,
  text,
  size = 'large',
}: MagicButtonProps) => {
  const { userData } = useAuth();
  const { customer } = userData;
  const isAutomotiveServiceRole = userData.isAutomotiveServiceRole;

  const [showModalGenerateText, setShowModalGenerateText] = useState(false);

  const handleModalOpen = () => setShowModalGenerateText(true);
  const handleModalClose = () => setShowModalGenerateText(false);

  const enableCovideoMagic =
    !isAutomotiveServiceRole && !!Number(customer.hasCovideoMagic);

  if (!enableCovideoMagic) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div style={{ margin: margin }} className="ai-assist">
        {!!hoverPopup ? (
          <HoverPopup
            showtriangle
            position={hoverPosition}
            padding={'0'}
            width="auto"
            hoverable={
              <Button
                onClick={handleModalOpen}
                variant={variant}
                text={text}
                size={size}
                disabled={disabled}
                icon={
                  iconColor === 'gradient' ? (
                    <IconSparkleGradient />
                  ) : (
                    <IconSparkle color={iconColor} />
                  )
                }
              />
            }
            popup={hoverPopup}
          />
        ) : (
          <Button
            onClick={handleModalOpen}
            variant={variant}
            text={text}
            size={size}
            disabled={disabled}
            icon={
              iconColor === 'gradient' ? (
                <IconSparkleGradient />
              ) : (
                <IconSparkle color={iconColor} />
              )
            }
          />
        )}
        {showModalGenerateText && (
          <AiProvider
            defaultPromptType={defaultPromptType}
            defaultPromptUseCase={defaultPromptUseCase}
            defaultRecipient={defaultRecipient}
            defaultVin={defaultVin}
            userInput={userInput}
            videoId={videoId}
          >
            <ModalGenerateText
              closeModal={handleModalClose}
              handleSubmitGeneratedContent={handleSubmitGeneratedContent}
              handleOnCopySuccess={handleOnCopySuccess}
            />
          </AiProvider>
        )}
      </div>
    </QueryClientProvider>
  );
};
